import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  List,
  ListSubheader,
  ListItem,
  CircularProgress,
} from '@mui/material';
import { dashboardsApi, metricsApi } from '../services/api';
import { MetricWithValue } from '../types';
import { Dashboard } from '../types';

interface CreateCustomDashboardDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (dashboard: Dashboard) => void;
  isMixed: boolean;
  departmentId: number | null;
  defaultName?: string;
}

const CreateCustomDashboardDialog: React.FC<CreateCustomDashboardDialogProps> = ({
  open,
  onClose,
  onSuccess,
  isMixed,
  departmentId,
  defaultName = '',
}) => {
  const [name, setName] = useState(defaultName || '');
  const [metrics, setMetrics] = useState<MetricWithValue[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(defaultName || (isMixed ? 'Custom view' : 'Custom dashboard'));
      setSelectedIds(new Set());
      setLoadError(null);
      const load = async () => {
        setLoading(true);
        try {
          const data = await metricsApi.getAll();
          setMetrics(data);
        } catch (e: any) {
          setLoadError(e.message || 'Failed to load metrics');
        } finally {
          setLoading(false);
        }
      };
      load();
    }
  }, [open, isMixed, defaultName]);

  const handleToggle = (metricId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(metricId)) next.delete(metricId);
      else next.add(metricId);
      return next;
    });
  };

  const handleSelectAllInDepartment = (departmentName: string | null) => {
    const inDept = byDepartment[departmentName ?? 'No department'] ?? [];
    const allSelected = inDept.every((m) => selectedIds.has(m.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      inDept.forEach((m) => {
        if (allSelected) next.delete(m.id);
        else next.add(m.id);
      });
      return next;
    });
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const metricIds = Array.from(selectedIds);
    if (metricIds.length === 0) return;

    setSaving(true);
    try {
      const created = await dashboardsApi.create(trimmed, {
        department_id: departmentId,
        is_mixed: isMixed,
      });
      await dashboardsApi.setMetrics(created.id, metricIds);
      onSuccess(created);
      onClose();
    } catch (e: any) {
      setLoadError(e.message || 'Failed to create dashboard');
    } finally {
      setSaving(false);
    }
  };

  const canonicalByKey = new Map<string, MetricWithValue>();
  metrics.forEach((m) => {
    const key = `${m.name}\n${m.department_name ?? ''}`;
    const existing = canonicalByKey.get(key);
    if (!existing || m.id < existing.id) canonicalByKey.set(key, m);
  });
  const displayMetrics = Array.from(canonicalByKey.values());
  const byDepartment = displayMetrics.reduce<Record<string, MetricWithValue[]>>((acc, m) => {
    const key = m.department_name || 'No department';
    if (!acc[key]) acc[key] = [];
    acc[key].push(m);
    return acc;
  }, {});
  const departmentNames = Object.keys(byDepartment).sort();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add custom dashboard</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Dashboard name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sales + Finance view"
            fullWidth
            autoFocus
          />
          <Typography variant="subtitle2" color="text.secondary">
            Choose which metrics to show in this table view. They will keep their quarterly targets from their primary boards.
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : loadError ? (
            <Typography color="error">{loadError}</Typography>
          ) : (
            <List
              dense
              sx={{
                maxHeight: 360,
                overflow: 'auto',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {departmentNames.map((deptName) => (
                <React.Fragment key={deptName}>
                  <ListSubheader
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: 'background.paper',
                      py: 0.5,
                    }}
                  >
                    <FormControlLabel
                      label={deptName}
                      control={
                        <Checkbox
                          size="small"
                          checked={byDepartment[deptName].every((m) => selectedIds.has(m.id))}
                          indeterminate={
                            byDepartment[deptName].some((m) => selectedIds.has(m.id)) &&
                            !byDepartment[deptName].every((m) => selectedIds.has(m.id))
                          }
                          onChange={() => handleSelectAllInDepartment(deptName)}
                        />
                      }
                    />
                  </ListSubheader>
                  {byDepartment[deptName].map((m) => (
                    <ListItem key={m.id} dense disablePadding sx={{ pl: 4 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedIds.has(m.id)}
                            onChange={() => handleToggle(m.id)}
                          />
                        }
                        label={m.name}
                      />
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !name.trim() || selectedIds.size === 0 || loading}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCustomDashboardDialog;
